import React, { useEffect, useMemo, useState } from "react";
import { api } from "../lib/api";
import { PageHeader, PageBody, EmptyState, formatMoney } from "../components/Shared";
import { Link, useNavigate } from "react-router-dom";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Button } from "../components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Ship, Plus } from "lucide-react";
import { toast } from "sonner";

export const SHIPMENT_STATUSES = ["order_placed", "in_transit", "customs", "to_warehouse", "in_warehouse", "shipped_to_customer"];
export const SHIPMENT_STATUS_LABELS = {
  order_placed: "Sipariş açıldı",
  in_transit: "Yolda",
  customs: "Gümrükte",
  to_warehouse: "Depoya sevk ediliyor",
  in_warehouse: "Depoda",
  shipped_to_customer: "Müşteriye sevk edildi",
};
const SHIPMENT_COLORS = {
  order_placed: "bg-sky-50 text-sky-700 ring-sky-700/10",
  in_transit: "bg-indigo-50 text-indigo-700 ring-indigo-700/10",
  customs: "bg-amber-50 text-amber-700 ring-amber-700/10",
  to_warehouse: "bg-cyan-50 text-cyan-700 ring-cyan-700/10",
  in_warehouse: "bg-emerald-50 text-emerald-700 ring-emerald-700/10",
  shipped_to_customer: "bg-teal-50 text-teal-700 ring-teal-700/10",
};

export function ShipmentStatusBadge({ status }) {
  return (
    <span
      className={`inline-flex items-center px-2 py-0.5 text-xs font-mono uppercase tracking-wider ring-1 ring-inset ${SHIPMENT_COLORS[status] || "bg-slate-100 text-slate-700 ring-slate-700/10"}`}
      data-testid={`shipment-badge-${status}`}
    >
      {SHIPMENT_STATUS_LABELS[status] || status}
    </span>
  );
}

export default function Imports() {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [status, setStatus] = useState("all");
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", notes: "" });
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/shipments", { params: status === "all" ? {} : { status } }).then((r) => setRows(r.data));
  useEffect(() => { load(); }, [status]); // eslint-disable-line react-hooks/exhaustive-deps

  const filtered = useMemo(() => {
    if (!q) return rows;
    const s = q.toLowerCase();
    return rows.filter((r) =>
      r.name?.toLowerCase().includes(s) ||
      (r.items || []).some((i) => (i.part_number || "").toLowerCase().includes(s))
    );
  }, [rows, q]);

  const create = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) return toast.error("Yükleme adı gerekli");
    setBusy(true);
    try {
      const { data } = await api.post("/shipments", { name: form.name.trim(), notes: form.notes || null, items: [] });
      toast.success("Yükleme oluşturuldu");
      setOpen(false);
      setForm({ name: "", notes: "" });
      navigate(`/imports/${data.shipment.id}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Oluşturulamadı");
    } finally {
      setBusy(false);
    }
  };

  const quickStatus = async (row, st) => {
    try {
      await api.put(`/shipments/${row.id}/status`, { status: st });
      toast.success(`${row.name} → ${SHIPMENT_STATUS_LABELS[st]}`);
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Durum güncellenemedi"); }
  };

  return (
    <>
      <PageHeader
        title="Imports"
        subtitle="İthalat yüklemeleri — sipariş kalemlerini yükleme altında takip edin"
        testid="imports-title"
        actions={
          <Button onClick={() => setOpen(true)} className="rounded-none bg-blue-600 hover:bg-blue-700" data-testid="new-shipment-btn">
            <Plus className="w-4 h-4 mr-2" /> New Shipment
          </Button>
        }
      />
      <PageBody>
        <div className="grid md:grid-cols-4 gap-3 mb-4 items-end">
          <div>
            <Label className="label-caps">Search</Label>
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Yükleme adı, part #…" className="rounded-none mt-1" data-testid="imports-search" />
          </div>
          <div>
            <Label className="label-caps">Durum</Label>
            <Select value={status} onValueChange={setStatus}>
              <SelectTrigger className="rounded-none mt-1" data-testid="imports-status-filter"><SelectValue /></SelectTrigger>
              <SelectContent className="rounded-none">
                <SelectItem value="all">Tümü</SelectItem>
                {SHIPMENT_STATUSES.map((s) => <SelectItem key={s} value={s}>{SHIPMENT_STATUS_LABELS[s]}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>

        {filtered.length === 0 ? <EmptyState title="Henüz yükleme yok" hint="New Shipment ile başlayın; ürünleri Order Items sayfasından seçip ekleyebilirsiniz." /> :
          <div className="bg-white border border-slate-300 overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 border-b border-slate-300"><tr className="text-left">
                <th className="py-2 px-3 label-caps">Yükleme adı</th>
                <th className="py-2 px-3 label-caps">Durum</th>
                <th className="py-2 px-3 label-caps text-right">Kalem</th>
                <th className="py-2 px-3 label-caps text-right">Adet</th>
                <th className="py-2 px-3 label-caps text-right">Tutar</th>
                <th className="py-2 px-3 label-caps text-right">Maliyet</th>
                <th className="py-2 px-3 label-caps">Oluşturma</th>
                <th className="py-2 px-3 label-caps">Oluşturan</th>
              </tr></thead>
              <tbody>
                {filtered.map((r) => (
                  <tr key={r.id} className="border-b border-slate-200 hover:bg-slate-50 stagger-row" data-testid={`shipment-row-${r.id}`}>
                    <td className="py-2 px-3 font-mono">
                      <Link to={`/imports/${r.id}`} className="text-blue-700 hover:text-blue-900" data-testid={`shipment-link-${r.id}`}>{r.name}</Link>
                    </td>
                    <td className="py-2 px-3">
                      <div className="flex items-center gap-2">
                        <ShipmentStatusBadge status={r.status} />
                        <Select value={r.status} onValueChange={(v) => quickStatus(r, v)}>
                          <SelectTrigger className="rounded-none h-7 w-40 text-xs" data-testid={`shipment-status-${r.id}`}><SelectValue /></SelectTrigger>
                          <SelectContent className="rounded-none">
                            {SHIPMENT_STATUSES.map((s) => <SelectItem key={s} value={s}>{SHIPMENT_STATUS_LABELS[s]}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </div>
                    </td>
                    <td className="py-2 px-3 font-mono text-right">{r.item_count}</td>
                    <td className="py-2 px-3 font-mono text-right">{r.total_qty}</td>
                    <td className="py-2 px-3 font-mono text-right">
                      {Object.entries(r.totals || {}).map(([c, v]) => <div key={c}>{formatMoney(v, c)}</div>)}
                    </td>
                    <td className="py-2 px-3 font-mono text-right" data-testid={`shipment-cost-${r.id}`}>
                      {(r.missing_cost_count || 0) > 0 && (
                        <span className="inline-block px-2 py-0.5 border border-amber-300 bg-amber-50 text-amber-800 text-[10px] font-medium uppercase tracking-wider mb-1"
                              data-testid={`shipment-missing-cost-${r.id}`}
                              title="Alış fiyatı eksik kalem var — maliyet eksik hesaplanıyor">
                          {r.missing_cost_count} kalem eksik
                        </span>
                      )}
                      {r.cost_normalized && (r.cost_normalized.try || r.cost_normalized.usd) ? (
                        <>
                          <div className="font-medium" data-testid={`shipment-cost-try-${r.id}`}>
                            {Number(r.cost_normalized.try).toLocaleString("tr-TR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ₺
                          </div>
                          <div className="text-[10px] text-slate-500">
                            ${Number(r.cost_normalized.usd).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                            {r.cost_normalized.locked && " 🔒"}
                          </div>
                        </>
                      ) : !Object.keys(r.cost_totals || {}).length ? (
                        <span className="text-slate-400">—</span>
                      ) : (
                        Object.entries(r.cost_totals || {}).map(([c, v]) => <div key={c}>{formatMoney(v, c)}</div>)
                      )}
                    </td>
                    <td className="py-2 px-3 text-xs text-slate-500">{(r.created_at || "").slice(0, 10)}</td>
                    <td className="py-2 px-3 text-xs text-slate-500">{r.created_by}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        }
      </PageBody>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-none max-w-md" data-testid="new-shipment-dialog">
          <DialogHeader><DialogTitle className="font-display tracking-tight flex items-center gap-2"><Ship className="w-5 h-5 text-blue-700" /> Yeni Yükleme</DialogTitle></DialogHeader>
          <form onSubmit={create} className="space-y-3">
            <div>
              <Label className="label-caps">Yükleme adı *</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="örn. Konteyner EK-2026-04" className="rounded-none mt-1" data-testid="shipment-name" autoFocus />
            </div>
            <div>
              <Label className="label-caps">Not</Label>
              <Input value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="opsiyonel" className="rounded-none mt-1" data-testid="shipment-notes" />
            </div>
            <div className="text-xs text-slate-500">Ürünleri sonra ekleyebilirsiniz: Order Items sayfasından seçip "Add to shipment" ile veya yükleme detayındaki "Add items" butonuyla.</div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setOpen(false)} className="rounded-none" data-testid="shipment-cancel">Vazgeç</Button>
              <Button type="submit" disabled={busy} className="rounded-none bg-blue-600 hover:bg-blue-700" data-testid="shipment-create">Oluştur</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </>
  );
}
